/**
 * Import Progress Indicator — recently-completed import that opened an
 * unknown-rows auto-ticket (issue #305).
 *
 * Extracted from ImportProgressIndicator.tsx. The copy + testIds mirror the
 * editor's ResultsPreviewSection banner verbatim so existing
 * `unknown-rows-ticket-banner` selectors keep working.
 */

import { CheckCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { UnknownRowsTicketBanner } from '@/modules/accounting/features/import/components/custom-importer/sections/ResultsPreviewSection'
import { ImportItemHeader } from './ImportItemHeader'

export interface CompletedImportWithTicket {
  jobId: string
  sourceName: string
  completedAt: Date
  ticket: UnknownRowsTicketBanner
}

interface CompletedTicketItemProps {
  entry: CompletedImportWithTicket
  onDismiss: () => void
  formatRelativeTime: (date: Date) => string
}

export const CompletedTicketItem = ({
  entry,
  onDismiss,
  formatRelativeTime,
}: CompletedTicketItemProps) => {
  const { t } = useTranslation('imports')
  const { ticket } = entry

  return (
    <div
      className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 space-y-2"
      data-testid={`completed-ticket-item-${entry.jobId}`}
    >
      <ImportItemHeader
        icon={<CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />}
        sourceName={entry.sourceName}
        timestampLabel={formatRelativeTime(entry.completedAt)}
        onDismiss={onDismiss}
        dismissTestId={`completed-ticket-dismiss-${entry.jobId}`}
        dismissHoverClassName="hover:bg-amber-500/20"
      />

      <div
        /* `text-amber-200` only reads on a DARK surface. The chip
           (`bg-amber-500/10`) is TRANSLUCENT, so in the light theme it
           composites over the white page and this explanation measured 1.15:1 —
           the user could not read WHY their import booked 0 transactions.
           The base + `dark:` pair is left byte-identical (dark theme and the
           pre-hydration no-`data-theme` state are unchanged); the light theme
           gets its own darker swatch. */
        className="text-xs text-amber-200 dark:text-amber-200 [html[data-theme=light]_&]:text-amber-800"
        data-testid="unknown-rows-ticket-banner"
        role="status"
      >
        <p data-testid="unknown-rows-ticket-banner-text">
          {/* ⚠️ `count` selects the plural form; `{{rowCount}}` stays as the
              interpolated placeholder so the sentences read the same. The
              keys USED to dodge pluralisation altogether — the comment here
              said `rowCount` avoided "the lookup for `*_one`/`*_other` keys we
              do not provide" — and the price was «{{rowCount}} fila(s)», which
              is not a plural in any of the two languages. Now the forms exist.

              The *CountUnknown variants carry no number at all (the backend
              re-upload fallback couldn't measure it, staging pruned), so they
              need no plural: showing nothing beats fabricating a count. */}
          {(() => {
            const isCountUnknown = ticket.unknownRowCount === null
            const key = isCountUnknown
              ? ticket.reused
                ? 'customImporter.unknownStatus.bannerReusedCountUnknown'
                : 'customImporter.unknownStatus.bannerCountUnknown'
              : ticket.reused
                ? 'customImporter.unknownStatus.bannerReused'
                : 'customImporter.unknownStatus.banner'
            return t(key, {
              count: ticket.unknownRowCount ?? 0,
              rowCount: ticket.unknownRowCount ?? 0,
              ticketRef: ticket.ticketReference,
            })
          })()}
        </p>
      </div>
    </div>
  )
}
